import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import { Grid, InputAdornment, Stack, SxProps, Typography } from '@mui/material'
import TextField from '@mui/material/TextField'
import React, { useState } from 'react'
import { Control, Controller, FieldValues } from 'react-hook-form'
import { InputProps } from '../../../store/types'
import StyledSpinButton from '../StyledSpinButton'

type TextInputProps = {
  maxLength?: number
  required?: boolean
  minValue?: number
  maxValue?: number
  numericType?: NumericType
  unitOfMeasure?: string
  decimalPrecision?: number
  sxProps?: SxProps
  hideHelperText?: boolean
  showSpinButtons?: boolean
}
type NumericType = 'natural' | 'decimal' | 'integer' | 'float' | undefined

const validateTypeOnChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  onChange: (value: string) => void,
  onDisplayValueChange: (displayValue: string) => void,
  numericType: NumericType,
  precision?: number,
  name?: string,
  hideHelperText?: boolean
) => {
  let value = e.target.value
  let displayValue = value

  switch (numericType) {
    case 'natural':
      value = value.replace(/\D/g, '').replace(/^0+(?!$)/, '')
      displayValue = value
      break
    case 'decimal':
      value = value.replace(/[^0-9,.]/g, '')
      displayValue = value
      const parts = value.split(/[,.]/)
      const separator = value.includes(',') ? ',' : '.'
      value = value.replace(',', '.')

      if (parts.length > 2) {
        const newValue = `${parts[0]}.${parts.slice(1).join('')}`
        value = newValue
        displayValue = newValue.replace('.', separator)
      }

      if (parts[1]?.length > (precision ? precision : 2)) {
        const newValue = `${parts[0]}.${parts[1].slice(0, precision ? precision : 2)}`
        value = newValue
        displayValue = newValue.replace('.', separator)
      }

      break
    case 'integer':
      value = value.replace(/[^0-9-]/g, '')
      displayValue = value
      break
    case 'float':
      value = value.replace(/[^0-9.-]/g, '')
      displayValue = value
      break
    default:
      break
  }

  onChange(value)
  onDisplayValueChange(displayValue)
}

const NumericInput = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  rules,
  maxLength,
  required = false,
  disabled,
  minValue,
  maxValue,
  numericType,
  unitOfMeasure,
  decimalPrecision,
  sxProps,
  hideHelperText,
  showSpinButtons = false,
  ...textFieldProps
}: InputProps<T> & TextInputProps) => {
  const [displayValue, setDisplayValue] = useState('')
  const handleDisplayValueChange = (displayValue: string) => setDisplayValue(displayValue)

  const handleStepChange = (
    currentValue: string | undefined,
    onChange: (value: string) => void,
    direction: 'increment' | 'decrement'
  ) => {
    const numValue = currentValue ? parseFloat(currentValue) : 0
    const step = numericType === 'decimal' || numericType === 'float' ? 0.1 : 1
    const newValue = direction === 'increment' ? numValue + step : numValue - step

    const isWithinBounds =
      direction === 'increment'
        ? maxValue === undefined || newValue <= maxValue
        : minValue === undefined || newValue >= minValue

    if (isWithinBounds) {
      const formattedValue =
        numericType === 'decimal' || numericType === 'float'
          ? newValue.toFixed(decimalPrecision || 2)
          : newValue.toString()
      onChange(formattedValue)
      setDisplayValue(formattedValue)
    }
  }

  return (
    <Controller
      name={name}
      control={control as Control<T>}
      rules={rules}
      render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
        <TextField
          id={`text-input-${name}`}
          aria-label={label}
          required={required}
          sx={sxProps}
          size="small"
          placeholder={placeholder}
          error={!!error}
          onChange={e => {
            validateTypeOnChange(
              e,
              onChange,
              handleDisplayValueChange,
              numericType as NumericType,
              decimalPrecision,
              label
            )
          }}
          onBlur={onBlur}
          value={value != null && !displayValue.length ? value : displayValue}
          disabled={disabled}
          fullWidth
          label={label}
          variant="outlined"
          {...textFieldProps}
          inputProps={{ min: minValue, max: maxValue, maxLength: maxLength }}
          InputProps={{
            endAdornment: (
              <>
                {showSpinButtons && !disabled && (
                  <InputAdornment position="end">
                    <Stack direction="column">
                      <StyledSpinButton
                        onClick={() => handleStepChange(value, onChange, 'increment')}
                        disabled={maxValue !== undefined && parseFloat(value || '0') >= maxValue}
                      >
                        <ArrowDropUp sx={{ fontSize: 24, mt: 0.5 }} />
                      </StyledSpinButton>
                      <StyledSpinButton
                        onClick={() => handleStepChange(value, onChange, 'decrement')}
                        disabled={minValue !== undefined && parseFloat(value || '0') <= minValue}
                      >
                        <ArrowDropDown sx={{ fontSize: 24, mb: 0.5 }} />
                      </StyledSpinButton>
                    </Stack>
                  </InputAdornment>
                )}
                {unitOfMeasure && <InputAdornment position="end">{unitOfMeasure}</InputAdornment>}
              </>
            ),
          }}
          helperText={
            !hideHelperText && error ? (
              <Grid component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="caption">{error ? error.message : null}</Typography>
              </Grid>
            ) : null
          }
        />
      )}
    />
  )
}

export default NumericInput
